<template>
  <ModalStructure
    data-test-id="UserGroupDetailsModal"
    hasBlur
    :showModal="showResourceGroupModal"
    showCloseButton
    @close="handleClose"
  >
    <template v-slot:header>
      <HeadingLarge>
        {{ modalHeader }}
      </HeadingLarge>
    </template>
    <template v-slot:body>
      <div>
        <HeadingSmall>
          Assigned Resources
        </HeadingSmall>
        <UserGroupResource
          :class="$style.userGroupResource"
          :config="$options.applicationUserGroupConfig"
          data-test-id="UserGroupDetailsModal__applicationResources"
          :isLoading="isLoading"
          :resourceData="applicationsData"
        />
        <UserGroupResource
          :class="$style.userGroupResource"
          :config="$options.radiusUserGroupConfig"
          data-test-id="UserGroupDetailsModal__radiusServerResources"
          :isLoading="isLoading"
          :resourceData="radiusServerData"
        />
        <UserGroupResource
          :class="$style.userGroupResource"
          :config="$options.deviceUserGroupConfig"
          data-test-id="UserGroupDetailsModal__deviceGroupResources"
          :isLoading="isLoading"
          :resourceData="devicesGroupsData"
          :totalCount="totalDeviceCount"
        />
        <UserGroupResource
          :class="$style.userGroupResource"
          :config="$options.directoryUserGroupConfig"
          data-test-id="UserGroupDetailsModal__directoryResources"
          :isLoading="isLoading"
          :resourceData="directoriesData"
        />
      </div>
    </template>
    <template v-slot:footer>
      <Button
        text="Ok"
        @click="handleClose"
      />
    </template>
  </ModalStructure>
</template>

<script>
import {
  Button,
  HeadingLarge,
  HeadingSmall,
  ModalStructure,
  Types,
} from '@jumpcloud/ui-components';

import {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModelMap,
} from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import ItemType from 'client/util/Constants/ItemType';
import UserGroupResource from './UserGroupResource.vue';

const { PrimaryButtonProps } = Types;
const primaryButtonProps = PrimaryButtonProps({ text: 'Ok' });
const userGroupModel = StoreModelMap[ItemTypeConsts.userGroup];
const userGroupCollection = 'UserGroupCollection';

const {
  ApplicationItemType,
  DeviceGroupItemType,
  DirectoryItemType,
  RadiusServerItemType,
} = ItemType;

const applicationUserGroupItem = new ApplicationItemType();
const directoryUserGroupItemType = new DirectoryItemType();
const deviceGroupItemType = new DeviceGroupItemType();
const radiusUserGroupItemType = new RadiusServerItemType();

export default {
  name: 'UserGroupDetailsModal',

  components: {
    Button,
    HeadingLarge,
    HeadingSmall,
    ModalStructure,
    UserGroupResource,
  },

  props: {
    selectedGroupId: {
      type: String,
      default: '',
    },

    selectedGroupName: {
      type: String,
      default: '',
    },

    showResourceGroupModal: Boolean,
  },

  data() {
    return {
      applicationsList: [],
      deviceGroupsList: [],
      directoriesList: [],
      isLoading: true,
      radiusServerList: [],
      totalCount: 0,
    };
  },

  applicationUserGroupConfig: {
    heading: 'Applications',
    linkText: `Assign an ${applicationUserGroupItem.label}`,
    route: ItemTypeRoutes[ItemTypeConsts.application],
    zeroStateText: `No ${applicationUserGroupItem.labelPlural} have been directly assigned.`,
  },

  deviceUserGroupConfig: {
    heading: 'Device Groups',
    linkText: `Assign a ${deviceGroupItemType.label}`,
    route: ItemTypeRoutes[ItemTypeConsts.deviceGroup],
    zeroStateText: `No ${deviceGroupItemType.labelPlural} have been directly assigned.`,
  },

  directoryUserGroupConfig: {
    heading: 'Directories',
    linkText: `Assign a ${directoryUserGroupItemType.label}`,
    route: ItemTypeRoutes[ItemTypeConsts.directory],
    zeroStateText: `No ${directoryUserGroupItemType.labelPlural} have been directly assigned.`,
  },

  radiusUserGroupConfig: {
    heading: 'RADIUS',
    linkText: `Assign a ${radiusUserGroupItemType.label}`,
    route: ItemTypeRoutes[ItemTypeConsts.radiusServer],
    zeroStateText: `No ${radiusUserGroupItemType.labelPlural} have been directly assigned.`,
  },

  primaryButtonProps,

  computed: {
    ...mapGetters(userGroupModel, [
      'getTotalDeviceGroupCount',
    ]),

    applicationsData() {
      return this.applicationsList.join(', ');
    },

    devicesGroupsData() {
      return this.deviceGroupsList.join(', ');
    },

    directoriesData() {
      return this.directoriesList.join(', ');
    },

    modalHeader() {
      return `${this.selectedGroupName} Group Preview`;
    },

    radiusServerData() {
      return this.radiusServerList.join(', ');
    },

    totalDeviceCount() {
      return this.getTotalDeviceGroupCount;
    },

  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions('AlertModule', ['triggerErrorAlert']),

    ...mapActions(userGroupCollection, {
      fetchGroupSummaryResources: 'fetchGroupSummaryResources',
    }),

    async fetchSelectedGroupResources() {
      try {
        const {
          application,
          directories,
          radiusServers,
          systemGroups,
        } = await this.fetchGroupSummaryResources({ id: this.selectedGroupId });

        application.forEach((resource) => {
          this.applicationsList.push(resource.displayLabel);
        });

        directories.forEach((resource) => {
          this.directoriesList.push(resource.name);
        });

        radiusServers.forEach((resource) => {
          this.radiusServerList.push(resource.name);
        });

        systemGroups.forEach((resource) => {
          this.deviceGroupsList.push(resource.name);
        });

        // Make sure the lists are sorted
        this.applicationsList.sort();
        this.deviceGroupsList.sort();
        this.directoriesList.sort();
        this.radiusServerList.sort();
      } catch (error) {
        this.triggerErrorAlert(`Unable to retrieve ${get(this.item, 'name', 'group')} data. Please refresh the page and try again.`);
      } finally {
        this.isLoading = false;
      }
    },

    handleClose() {
      this.$emit('close');
    },

    handleMounted() {
      this.fetchSelectedGroupResources();
    },

  },
};
</script>

<style module scoped>
.userGroupResource:not(:last-child) {
  margin-bottom: var(--jc-spacer-medium);
}
</style>
